import React, { useCallback, useEffect, useState } from "react";
import CardTable from "../../../components/AdminTable/CardTable";
import axios from "axios";
import { apiKey } from "../../../config/config";
import handleCSRF from "../../../config/CSRF";
import { toast } from "react-toastify";
import { FaEdit, FaTrashAlt, FaCog } from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { Link } from "react-router-dom";
import { languageHelper } from "../../../config/helperFunctions";



function CheckBox({ row, user, status, handleError }) {
  const initialChecked = row.getValue() === "active";
  const [isChecked, setIsChecked] = useState(initialChecked);
  const [isLoading, setIsLoading] = useState(false);

  const handleUpdate = (e) => {
    const newChecked = e.target.checked;
    setIsLoading(true);

    axios
      .put(
        `${apiKey}/api/rms/admin/categories/${row.row.original.id}`,
        {
          [status]: newChecked ? "active" : "inactive",
        },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      )
      .then((res) => {
        toast.success(res.data.message, {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        setIsChecked(newChecked);
      })
      .catch((err) => {
        handleError(err.response.data.message);
        toast.error(err.response.data.message, {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="relative">
      <input
        type="checkbox"
        onChange={handleUpdate}
        checked={isChecked}
        className="theme-checkbox"
      />
      {isLoading && (
        <span className="absolute top-0 left-0 w-full h-full py-1 px-3 bg-black bg-opacity-50 z-10 rounded-full">
          <img
            className="w-4 mx-auto"
            src="/assets/gif/button_loader.gif"
            alt=""
          />
        </span>
      )}
    </div>
  );
}

function PriorityInput({ row, user }) {
  const [value, setValue] = useState(row.getValue() ?? 0);
  const [isLoading, setIsLoading] = useState(false);

  const handleSave = () => {
    const normalized = Number(value) || 0;
    setValue(normalized);
    if (normalized === row.getValue()) return;
    setIsLoading(true);

    axios
      .put(
        `${apiKey}/api/rms/admin/categories/${row.row.original.id}`,
        { is_priority: normalized },
        {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        }
      )
      .then((res) => {
        toast.success(res.data.message || "Priority updated", {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      })
      .catch((err) => {
        toast.error(err.response?.data?.message || "Error", {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      })
      .finally(() => setIsLoading(false));
  };

  return (
    <div className="relative">
      <input
        type="number"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
        className="w-16 px-2 py-1 text-center border rounded text-sm outline-none focus:shadow-outline"
      />
      {isLoading && (
        <span className="absolute top-0 left-0 w-full h-full py-1 px-3 bg-black bg-opacity-50 z-10 rounded">
          <img
            className="w-4 mx-auto"
            src="/assets/gif/button_loader.gif"
            alt=""
          />
        </span>
      )}
    </div>
  );
}

function MenuCategories() {
  const user = JSON.parse(localStorage.getItem("rms-admin"));
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState([]);

  const columns = [
    {
      header: languageHelper("SN") || "SN",
      cell: (row) => row.row.index + 1,
    },
    {
      header: languageHelper("Category Name") || "Category Name",
      accessorKey: "category_name",
    },
    {
      header:
        languageHelper("Category Name Arabic") ||
        "Category Name Arabic",
      accessorKey: "cat_name_arabic",
    },
    {
      header: languageHelper("Description") || "Description",
      accessorKey: "description",
    },
    {
      header: languageHelper("Printer Name") || "Printer Name",
      accessorKey: "add_port",
    },
    {
      header: languageHelper("Web Status") || "Web Status",
      accessorKey: "web_status",
      cell: (row) => (
        <CheckBox
          key={`web_status_${row.row.original.id}`}
          row={row}
          status={"web_status"}
          user={user}
          handleError={handleError}
        />
      ),
    },
    {
      header: languageHelper("Pos Status") || "Pos Status",
      accessorKey: "status",
      cell: (row) => (
        <CheckBox
          key={`status_${row.row.original.id}`}
          row={row}
          status={"status"}
          user={user}
          handleLoading={handleLoading}
          handleError={handleError}
        />
      ),
    },
    {
      header:
        languageHelper("Subscription Status") ||
        "Subscription Status",
      accessorKey: "subscriptions_status",
      cell: (row) => (
        <CheckBox
          key={`subscriptions_status_${row.row.original.id}`}
          row={row}
          status={"subscriptions_status"}
          user={user}
          handleLoading={handleLoading}
          handleError={handleError}
        />
      ),
    },
    {
      header: languageHelper("Priority") || "Priority",
      accessorKey: "is_priority",
      cell: (row) => (
        <PriorityInput
          key={`is_priority_${row.row.original.id}`}
          row={row}
          user={user}
        />
      ),
    },
    {
      header: languageHelper("Actions") || "Actions",
      cell: (row) => (
        <ActionDropdown
          id={row.row.original.id}
          user={user}
          refetchData={fetchData}
        />
      ),
    },
  ];

  const buttonData = [
    {
      buttonText:
        languageHelper("Add Main Category") || "Add Main Category",
      path: "addMenuCategoryForm",
    },
  ];

  const handleLoading = (value) => {
    setPageLoading(value);
  };

  const handleError = (error) => {
    setError(error);
  };

  const fetchData = useCallback(
    (csrfToken) => {
      setPageLoading(true);
      axios
        .get(`${apiKey}/api/rms/admin/categories`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
          params: {
            outletId: user?.user.outlet_id,
          },
        })
        .then((res) => {
          setData(res.data);
        })
        .catch((err) => {
          setError(err.response.data.message);
        })
        .finally(() => {
          setPageLoading(false);
        });
    },
    [user?.token, user?.user.outlet_id]
  );
  useEffect(() => {
    handleCSRF(fetchData);
  }, [fetchData, user.token]);

  return (
    <main>
      <CardTable
        data={data}
        columns={columns}
        error={error}
        heading={languageHelper("Main Categories")}
        buttonData={buttonData}
      />
      {pageLoading && (
        <div className="absolute top-0 left-0 w-full h-full grid place-items-center">
          <img src="/assets/gif/dash2.gif" alt="loading..." />
        </div>
      )}
    </main>
  );
}

function ActionDropdown({ id, user, refetchData }) {
  const [showDropdown, setShowDropDown] = useState(false);
  const [showDelModal, setShowDelModal] = useState(false);

  const handleDelete = () => {
    axios
      .delete(`${apiKey}/api/rms/admin/categories/${id}`, {
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
      })
      .then((res) => {
        toast.success("Success", {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
        refetchData(); // Refetch data after successful delete
        setShowDelModal(false);
        setShowDropDown(false);
      })
      .catch((err) => {
        toast.error(err.response.data.message, {
          position: "bottom-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        });
      });
  };
  return (
    <>
      <div className="relative">
        <button
          className="text-blueGray-500 px-3 flex items-center justify-between gap-1 border rounded-full"
          onClick={() => setShowDropDown(!showDropdown)}
        >
          <FaCog className="text-d-bg-text" />
          <IoIosArrowDown className="text-d-bg-text" />
        </button>
        <div
          className={
            (showDropdown ? "block " : "hidden ") +
            "absolute top-4 left-0 -translate-x-[75%] bg-white text-base z-10 float-left py-2 list-none text-left rounded shadow-lg min-w-40"
          }
        >
          <Link
            to={`addMenuCategoryForm/${id}`}
            className={
              "text-sm py-2 px-4 font-normal flex items-center gap-4 w-full whitespace-nowrap bg-transparent text-blueGray-700"
            }
          >
            <FaEdit />
            <p> {languageHelper("Edit")} </p>
          </Link>
          <button
            onClick={() => setShowDelModal(true)}
            className={
              "text-sm py-2 px-4 font-normal flex items-center gap-4 w-full whitespace-nowrap bg-transparent text-blueGray-700"
            }
          >
            <FaTrashAlt />
            <p> {languageHelper("Delete")} </p>
          </button>
        </div>
      </div>
      {showDelModal && (
        <div
          className="w-full h-screen overflow-y-auto grid place-items-center fixed top-0 left-0 bg-black bg-opacity-50 backdrop-blur-md p-4 z-10"
          onClick={() => setShowDelModal(false)}
        >
          <div
            className="p-4 rounded-lg bg-white max-w-md w-full space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="space-y-4">
              <h1 className="text-d-bg-text font-semibold text-xl text-center">
                {languageHelper("Alert!") || "Alert!"}
              </h1>
              <p className="text-center text-gray-700">
                {languageHelper("Are you sure?")}
              </p>
            </div>
            <div className="flex justify-center gap-4">
              <button
                className="bg-d-cat-btn text-white font-bold text-sm px-4 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center"
                onClick={() => setShowDelModal(false)}
              >
                {languageHelper("No") || "No"}
              </button>
              <button
                onClick={handleDelete}
                className="bg-d-cat-btn text-white font-bold text-sm px-4 py-1 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150 text-center"
              >
                {languageHelper("Yes") || "Yes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default MenuCategories;
